
import { BehaviorSubject } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { WizPopUp } from '../../../components/popup/wiz-popup';
import { IWizPop } from '../../../interfaces/iwiz-pop';
import { ToastManager } from './toast.manager';

/**
 * PopUp Manager
 * Static feature, raised by wizModule
 */
export class PopUpManager {

  /**
   * current Pops keys are pops'uuid
   * values are NOT instances, but popParameters. WizPopUp own is instance
   */
  public static currentPops: BehaviorSubject<Map<string, WizPopUp>> = new BehaviorSubject(new Map());

  constructor() {
  }

  /** base manager set in wizModule */
  public static dialog: MatDialog = null;

  /**
   *  closing pop, unregister
   * @param pop : WizPopUp to kill
   */
  private static unregisterPop(pop: WizPopUp) {
    PopUpManager.currentPops.getValue().delete(pop?.uuid); // delete from currentPops' pile
    PopUpManager.currentPops.next(PopUpManager.currentPops.getValue()); // update pile
  }

  /**
   * on showing a new pop, register
   * @param pop : WizPopUp to add to currentPops
   */
  private static registerPop(pop: WizPopUp) {
    PopUpManager.currentPops.next(PopUpManager.currentPops.getValue().set(pop.uuid, pop));
  }

  /**
   * want to show a pop (WizPopUp.show())
   * @param pop: WizPopUp
   */
   public static refresh( pop: WizPopUp): void {
      if (pop.dialogRef) { // this pop exist (it has an instance). So look in currentPops, no need to build a second time
        // for exemple : on locked mode, clic again and again on "show my pop" button...
        const meExist: WizPopUp = PopUpManager.currentPops.getValue().get(pop.uuid);
        if (meExist) {
          (pop.dialogRef.componentInstance as IWizPop).onShow(); // play 'onShow' method on pop
        }
      }
    }

  /**
   * want to show a pop (WizPopUp.show())
   * @param pop: WizPopUp
   */
  public static show( pop: WizPopUp): WizPopUp {
    if (pop.dialogRef) { // this pop exist (it has an instance). So look in currentPops, no need to build a second time
      // for exemple : on locked mode, clic again and again on "show my pop" button...
      const meExist: WizPopUp = PopUpManager.currentPops.getValue().get(pop.uuid);
      if (meExist) {
        return meExist; // i'm in, exit
      }
    }

    // ... else (brand new show)....

    pop.dialogRef = this.dialog.open(pop.classFactory, pop.dialogConfig); // create instance
    // set UUID, same as contentView
    pop.uuid = pop.dialogRef.componentInstance.uuid; // physical view (instance based on factory) define the uuid
    // set deep (pop of the pop)
    let maxLevel: number = 0; // base level = 0, no pop
    PopUpManager.currentPops.getValue().forEach((value: WizPopUp, key: string) => {
      if (value.deep > maxLevel) {
        maxLevel = value.deep;
      }
    });
    maxLevel++; // max pop + 1
    pop.deep = maxLevel;

    (pop.dialogRef.componentInstance as IWizPop).datas = pop.datas; // set datas
    (pop.dialogRef.componentInstance as IWizPop).callback = pop.callback; // set callback
    (pop.dialogRef.componentInstance as IWizPop).parameters = pop; // accesseur à la conf depuis la pop

    // put a listener for non-modal pops. They must be unregistered on mouseDownOutside
    pop.mouseDownOutsideCloseSubscriber = pop.dialogRef.beforeClosed().subscribe(result => {
      PopUpManager.nonModalHide(); // will remove from currentPops, but first, will play 'onHide' method on closing pop
    });

    PopUpManager.registerPop(pop); // register that pop
    (pop.dialogRef.componentInstance as IWizPop).onShow(); // play 'onShow' method on pop
    return pop;
  }

  /** manual ask for closure
   * @param who : popUp Params
   */
  public static hide(who: WizPopUp): WizPopUp {
    if(who.isToast) { // redirection des toasts.. au cas ou..
      console.log('toast Hide', 'mauvais appel');
      return ToastManager.hide(who);
    } else {
      return PopUpManager.doHide(who, true);
    }
  }

  /**  mouseDownOutside ask for closure (only max level pop)
   * @param none : only top pop will be closed
   */
  public static nonModalHide() {
    let maxLevel: number = 0; // get max level
    let toClosePop: WizPopUp = null; // to close pop
    PopUpManager.currentPops.getValue().forEach((value: WizPopUp, key: string) => {  // seeking to close pop
      if (value.deep > maxLevel) {
        maxLevel = value.deep;
        toClosePop = value;
      }
    });

    // console.log ('popMan autoHide');
    if (toClosePop) { // only if exist (may be previously deleted on clic)
      PopUpManager.doHide(toClosePop);
    } else {
      console.log ('that pop has gone');
    }

  }

  /**
   * really DO hide
   * @param who : WizPopUp   * @param doClose : force close (manual hide incoming. on nonModalHide, pop will close itself)
   */
  public static doHide(who: WizPopUp, doClose: boolean = false ): WizPopUp {

    const popID: string = who.uuid; // the popID (control stuff)
    const meMap: Map<string, WizPopUp> = PopUpManager.currentPops.getValue(); // the source Map (control stuff)
    const popParam: WizPopUp = meMap.get(popID); // the popParam (control stuff)
    // console.log ('popMan hide ' + popID); // control here, if needed

    if (popParam?.mouseDownOutsideCloseSubscriber) {
      popParam.mouseDownOutsideCloseSubscriber.unsubscribe(); // remove listener on nonModal hide, or will play twice (me and next, topPop)
    }

    if ( popParam?.dialogRef?.componentInstance) {
      (popParam.dialogRef.componentInstance as IWizPop).onHide(); // play 'onHide on closing pop (physical instance)
    }

    PopUpManager.unregisterPop(popParam); // remove from pile

    // manual hide incoming, close pop
    if (doClose) {
      popParam?.dialogRef.close(); // bye bye pop
    }
    return popParam;
  }

  /**
   * looking for existing pop
   * @param who : WizPopUp to look after
   */
  public static exist(who: WizPopUp): boolean {
    if (who) {
      const meExist: WizPopUp = PopUpManager.currentPops.getValue().get(who.uuid);
      if (meExist) {
        return true; // i'm in
      }
    }
    return false;
  }
}
